/**
 * percentileTone
 *
 * The colour of a feed's change percentile: a ramp from the panel's neutral grey,
 * through a muted yellow, to a bright green for the calendars that change most.
 *
 * **Why the direction is what it is.** The badge used to be red at the top
 * ("90th percentile or above") and amber in the middle — which reads the ranking
 * as a health check, where the top of the scale is a problem. It is not: a feed in
 * the hundredth percentile is a calendar that changes constantly, and detecting
 * those changes is the product. The ranking answers "how much does this calendar
 * move", and a customer looking at the list is usually looking for the busy ones.
 * So the top of the scale is the bright end, and the ramp runs grey → yellow →
 * green.
 *
 * **Why a ramp and not three classes.** The interesting question is "how busy is
 * this one, relative to the others", and a stepped palette answers it with three
 * buckets. The interpolation is continuous, which is also what makes it a pure
 * function that can be tested without a browser — the same reason failureTone is
 * one.
 *
 * The stops are literal colours rather than design tokens: the value is computed,
 * not selected, and a token can be swapped by a stylesheet while an interpolation
 * cannot. The grey is the same neutral the rest of the panel uses for "no
 * information", so a feed nobody has seen changes for does not look like a
 * verdict.
 */

/** PERCENTILE_RAMP_MAX is the percentile the ramp's top stop sits at. */
export const PERCENTILE_RAMP_MAX = 100;

/**
 * The ramp's stops, in sRGB. The middle stop is a dull mustard rather than a
 * saturated yellow: a warning yellow here would read as "something is wrong with
 * this feed", which is exactly the reading this ramp exists to avoid.
 */
const RAMP = [
  { at: 0, rgb: [154, 160, 171] }, // --text-muted
  { at: 50, rgb: [173, 157, 63] },
  { at: PERCENTILE_RAMP_MAX, rgb: [47, 191, 95] },
];

/** lerp mixes two colours; `t` is clamped to 0..1 by the caller. */
function lerp(from, to, t) {
  const channel = (index) => Math.round(from[index] + (to[index] - from[index]) * t);
  return [channel(0), channel(1), channel(2)];
}

/**
 * percentileRamp resolves a percentile in 0..100 to the ramp's channels.
 *
 * A missing or unparseable percentile is the neutral grey: the value ends up in a
 * style attribute, so anything the function cannot interpret has to become a
 * colour the browser understands. The component prints "—" for those rows rather
 * than a number; the grey keeps them visually out of the ranking instead of
 * pretending they sit at the bottom of it.
 */
export function percentileRamp(percentile) {
  const value = Number(percentile);
  const share = Number.isFinite(value) ? Math.min(Math.max(value, 0), PERCENTILE_RAMP_MAX) : 0;

  let rgb = RAMP[0].rgb;
  for (let index = 1; index < RAMP.length; index++) {
    const upper = RAMP[index];
    const lower = RAMP[index - 1];
    if (share <= upper.at) {
      const span = upper.at - lower.at;
      rgb = lerp(lower.rgb, upper.rgb, span === 0 ? 0 : (share - lower.at) / span);
      break;
    }
    rgb = upper.rgb;
  }
  return rgb;
}

/** percentileTone is the ramp's colour, as CSS. */
export function percentileTone(percentile) {
  const [r, g, b] = percentileRamp(percentile);
  return `rgb(${r}, ${g}, ${b})`;
}

/**
 * percentileTint is the badge's background: the ramp's colour at a low alpha, so
 * the row reads as tinted rather than as a solid block of colour. The alpha is
 * written into an rgba() rather than appended to a hex string, because the tint
 * has to derive from the same channels the text uses.
 */
export function percentileTint(percentile) {
  const [r, g, b] = percentileRamp(percentile);
  return `rgba(${r}, ${g}, ${b}, 0.12)`;
}

/**
 * percentileOrdinal prints a percentile as an ordinal.
 *
 * "93th" is what a plain suffix produces, and it is the kind of detail that makes
 * a screen look unfinished; 11, 12 and 13 are the cases the naive rule gets wrong.
 */
export function percentileOrdinal(percentile) {
  const value = Number(percentile);
  if (!Number.isFinite(value)) return "—";
  const whole = Math.trunc(value);
  const tens = Math.abs(whole) % 100;
  if (tens >= 11 && tens <= 13) return `${whole}th`;
  switch (Math.abs(whole) % 10) {
    case 1:
      return `${whole}st`;
    case 2:
      return `${whole}nd`;
    case 3:
      return `${whole}rd`;
    default:
      return `${whole}th`;
  }
}
